import { useState } from "react";
import type { ReactNode } from "react";
import { Popover } from "@headlessui/react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { HEADLESS_UI_POPOVER_ROOT } from "@/react/components/shared/uikit/PopoverPortal";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { PopoverPortalContext } from "./PopoverPortalContext";

export interface PopperWrapperProps {
  children: ReactNode;
  color: string;
  dataTestId?: string;
  popoverButtonClassName?: string;
}

export const PopperWrapper = ({
  children,
  color,
  dataTestId,
  popoverButtonClassName,
}: PopperWrapperProps) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  const buttonBackgroundColor = color || "#000000";

  return (
    <Popover as="div" className="relative flex">
      {/* eslint-disable-next-line @typescript-eslint/unbound-method -- Updating typescript-eslint, should be fixed when appropriate */}
      {({ open, close }) => {
        const contextProps = { open, onClose: close };
        const portalRoot = document.getElementById(HEADLESS_UI_POPOVER_ROOT);

        return (
          <PopoverPortalContext.Provider value={contextProps}>
            <Popover.Button
              ref={setReferenceElement}
              className={classNames(
                "border-primary size-7 rounded-lg border",
                popoverButtonClassName,
              )}
              style={{ backgroundColor: buttonBackgroundColor }}
              data-testid={dataTestId}
            />

            {createPortal(
              <Popover.Panel
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
                className="bg-primary border-primary z-[9999] flex w-[16.5rem] flex-col items-center justify-center gap-y-[1.063rem] rounded-lg border pb-4 shadow-2xl"
              >
                {children}
              </Popover.Panel>,
              portalRoot ?? document.body,
            )}
          </PopoverPortalContext.Provider>
        );
      }}
    </Popover>
  );
};
